import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, ArrowLeft, ShieldCheck } from 'lucide-react';
import BRAND from '../constants/brand';

// ---------------------------------------------------------------------------
// Shared split-screen frame for Login / Register / Vendor registration.
//
// Branding and value props on the left, the form on the right. `children` is
// the form; this owns none of the auth logic.
//
// The first highlight used to read "download files for Amazon, Flipkart, Meesho
// & Myntra". Only Meesho is switched on. The landing page has been careful
// about that from the start — it names Meesho as live and says the others are
// built but not enabled — and the signup page was quietly promising four
// marketplaces to the very people about to pay for one. Corrected, and phrased
// so the others can be added by editing one line when they go live.
// ---------------------------------------------------------------------------

const HIGHLIGHTS = [
  'Generate a product once and download a file ready to upload to Meesho',
  'Four marketplace-ready images, a title and a description, prepared for you',
  'Import from a shared vendor catalogue instead of sourcing and shooting stock',
  'See exactly which products are ready to list, at a glance'
];

export default function AuthLayout({ title, subtitle, children, wide = false }) {
  return (
    <div className="min-h-screen flex bg-canvas text-ink">
      {/* ── left: what this is ──────────────────────────────────────── */}
      <div className="hidden lg:flex lg:w-[42%] relative overflow-hidden bg-brand-gradient
                      text-white p-12 flex-col justify-between">
        <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-white/10" />
        <div className="absolute -bottom-32 -left-16 w-80 h-80 rounded-full bg-white/5" />

        <Link to="/" className="relative z-10">
          <span className="block text-2xl font-bold">{BRAND.name}</span>
        </Link>

        <div className="relative z-10">
          <h2 className="text-3xl font-bold leading-snug mb-8">
            Ai Generate a product once.
            <br />
            Download a listing that is ready to upload.
          </h2>
          <ul className="space-y-4">
            {HIGHLIGHTS.map((h) => (
              <li key={h} className="flex items-start gap-3 text-sm text-white/90">
                <CheckCircle2 size={18} className="shrink-0 mt-0.5 text-white/70" />
                {h}
              </li>
            ))}
          </ul>

          <p className="mt-8 inline-flex items-center gap-2 text-xs text-white/80
                        rounded-lg bg-white/10 px-3 py-2">
            <ShieldCheck size={14} className="shrink-0" />
            We never ask for your marketplace password.
          </p>
        </div>

        <p className="relative z-10 text-xs text-white/60">
          © {new Date().getFullYear()} {BRAND.name}. All rights reserved.
        </p>
      </div>

      {/* ── right: the form ─────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col justify-center px-6 py-12 sm:px-12 lg:px-16">
        <div className={`${wide ? 'max-w-2xl' : 'max-w-sm'} mx-auto w-full`}>
          <Link
            to="/"
            className="lg:hidden inline-flex items-center gap-1.5 text-sm font-semibold
                       text-brand-500 mb-8"
          >
            <ArrowLeft size={15} /> {BRAND.name}
          </Link>

          <h1 className="text-2xl font-bold mb-1">{title}</h1>
          <p className="text-ink-2 text-sm mb-8">{subtitle}</p>

          {children}
        </div>
      </div>
    </div>
  );
}
